import { useEffect, useState } from 'react';
import { PlanT } from '@segurodeceso/core/domain/plans';
import getPlansService from '@services/getPlans';
import { usePlansStore } from '@stores';
import { LeadPlanT } from '@templates/Quoter/Quoter';
import { quoterPlanClick, quoterPlanDetailClick } from '@utils/datalayer';

interface UseQuoterConfig {
  storeLoadingDuration?: number;
  fetchLoadingDuration?: number;
}

const DEFAULT_STORE_LOADING_DURATION = 300;
const DEFAULT_FETCH_LOADING_DURATION = 1000;

const useQuoter = (
  noInsuredMembers: number,
  selectedPlanId: number,
  setSelectedPlan: (plan: LeadPlanT) => void,
  {
    storeLoadingDuration = DEFAULT_STORE_LOADING_DURATION,
    fetchLoadingDuration = DEFAULT_FETCH_LOADING_DURATION,
  }: UseQuoterConfig = {}
) => {
  const [filteredPlans, setFilteredPlans] = useState<PlanT[]>([]);
  const { plans, setPlans } = usePlansStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [lastClickedPlan, setLastClickedPlan] = useState<number>(0);

  const getClickedPlan = () => {
    return filteredPlans.find((plan) => plan.planId === lastClickedPlan);
  };

  const getFromStorageOrFetch = async (signal: AbortSignal) => {
    const startTime = Date.now();
    setLoading(true);

    if (plans && plans.length > 0) {
      filterPlansByMembers(plans);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, storeLoadingDuration - elapsedTime);
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }
    } else {
      try {
        const fetchedPlans = await getPlansService(signal);
        setPlans(fetchedPlans);
        filterPlansByMembers(fetchedPlans);

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, fetchLoadingDuration - elapsedTime);
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch plans:', error);
        }
      }
    }

    setLoading(false);
  };

  const filterPlansByMembers = (plans: PlanT[]) => {
    const filtered = plans.filter((plan) => plan.dependants === noInsuredMembers - 1);
    setFilteredPlans(filtered);
  };

  const getLowestPricePlan = (planCode: string): number => {
    const plansByPlanCode = plans.filter((plan) => plan.planCode === planCode);
    const lowestPrice: number = plansByPlanCode.reduce((prev, current) => {
      return prev < current.prices.price ? prev : current.prices.price;
    }, Number.MAX_VALUE);

    if (lowestPrice === Number.MAX_VALUE) {
      return undefined;
    }

    return lowestPrice;
  };

  const onMoreDetails = (plan: PlanT) => {
    setLastClickedPlan(plan.planId);
    setModalOpen(true);

    quoterPlanDetailClick(plan);
  };

  const setNewSelectedPlan = (plan: PlanT) => {
    setSelectedPlan({
      productCode: plan.productCode.toString(),
      planId: plan.planId.toString(),
      planCode: plan.planCode,
      planName: plan.planName,
      coverages: plan.coverages,
      prices: {
        price: plan.prices?.price,
        priceWithDiscount: plan.prices?.priceWithDiscount,
      },
      sponsor: plan.sponsor,
    });
  };

  const onCardClick = (plan: PlanT) => {
    setNewSelectedPlan(plan);
    quoterPlanClick(plan);
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchPlans = async () => {
      await getFromStorageOrFetch(signal);
    };

    fetchPlans();

    return () => {
      controller.abort();
    };
  }, [noInsuredMembers]);

  useEffect(() => {
    if (selectedPlanId && plans?.length > 0) {
      const selectablePlans = plans.filter((plan) => plan.dependants === noInsuredMembers - 1);
      const currentPlan = selectablePlans.find((plan) => plan.planId === selectedPlanId);
      if (currentPlan) {
        setNewSelectedPlan(currentPlan);
      }
    }
  }, [noInsuredMembers, plans]);

  return {
    plans: filteredPlans,
    loading,
    modalOpen,
    setModalOpen,
    setLastClickedPlan,
    getClickedPlan,
    getLowestPricePlan,
    onMoreDetails,
    onCardClick,
  };
};

export default useQuoter;
